import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Tag,
  Space,
  Modal,
  message,
  Spin,
  Progress,
  Statistic,
  Typography,
  Alert,
  Tooltip,
  Result,
} from 'antd';
import {
  ReloadOutlined,
  CloudServerOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PoweroffOutlined,
  DatabaseOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  HddOutlined,
  LoadingOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { serverControlApi, setServerRestarting } from '../services/api';

const { Title, Text } = Typography;

interface ServiceStatus {
  container_name: string;
  status: string;
  status_detail: string;
  image: string;
}

interface VpsInfo {
  hostname: string;
  ip: string;
  node_location: string;
  os: string;
  ve_status: string;
  plan_ram: number;
  plan_disk: number;
  plan_monthly_data: number;
  data_counter: number;
  ve_used_disk_space_b: number;
  ve_disk_quota_gb: string;
}

interface ServerStatus {
  timestamp: string;
  docker_available: boolean;
  bwg_configured: boolean;
  services: Record<string, ServiceStatus>;
  vps: VpsInfo | null;
  docker_error?: string;
}

const SERVICE_LABELS: Record<string, { label: string; icon: React.ReactNode; description: string }> = {
  backend: { label: '后端服务', icon: <AppstoreOutlined />, description: 'FastAPI + Telegram Bot' },
  frontend: { label: '前端服务', icon: <GlobalOutlined />, description: 'React + Nginx' },
  postgres: { label: '数据库', icon: <DatabaseOutlined />, description: 'PostgreSQL 15' },
  redis: { label: '缓存', icon: <HddOutlined />, description: 'Redis 7' },
};

const STATUS_POLL_INTERVAL_MS = 30000;
const STATUS_RECOVERY_POLL_INTERVAL_MS = 10000;
const PING_POLL_INTERVAL_MS = 15000;

const ServerControl: React.FC = () => {
  const [status, setStatus] = useState<ServerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [restartingServices, setRestartingServices] = useState<Record<string, boolean>>({});
  const [restartingVps, setRestartingVps] = useState(false);
  const [restartingAll, setRestartingAll] = useState(false);
  const [withdrawalMaintenanceEnabled, setWithdrawalMaintenanceEnabled] = useState(false);
  const [withdrawalMaintenanceLoading, setWithdrawalMaintenanceLoading] = useState(false);
  // 重启等待遮罩
  const [waitingRestart, setWaitingRestart] = useState<string | null>(null); // null=正常, 'backend'/'all'/'vps'
  const [countdown, setCountdown] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const probeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPageVisible, setIsPageVisible] = useState(() => !document.hidden);

  // 延迟监控
  const [pingPaused, setPingPaused] = useState(false);
  const [latestPing, setLatestPing] = useState<Record<string, { latency_ms: number; status: string; error?: string }>>({});
  const [pingHistory, setPingHistory] = useState<Record<string, number[]>>({
    api: [], database: [], redis: [], telegram: [],
  });
  const pingHistoryRef = useRef<Record<string, number[]>>({
    api: [], database: [], redis: [], telegram: [],
  });
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [data, maintenance]: any = await Promise.all([
        serverControlApi.getStatus(),
        serverControlApi.getWithdrawalMaintenance(),
      ]);
      setStatus(data as any);
      setWithdrawalMaintenanceEnabled(Boolean(maintenance?.enabled));
    } catch {
      // 静默失败
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPing = useCallback(async () => {
    try {
      const data: any = await serverControlApi.ping();
      const endpoints = data.endpoints;
      setLatestPing(endpoints);
      const updated = { ...pingHistoryRef.current };
      for (const key of ['api', 'database', 'redis', 'telegram']) {
        const val = endpoints[key]?.latency_ms ?? -1;
        updated[key] = [...updated[key].slice(-59), val >= 0 ? val : 0];
      }
      pingHistoryRef.current = updated;
      setPingHistory({ ...updated });
    } catch {
      // 静默
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!isPageVisible || waitingRestart) return;

    fetchStatus();
    const timer = setInterval(fetchStatus, STATUS_POLL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      setServerRestarting(false);
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (probeTimeoutRef.current) clearTimeout(probeTimeoutRef.current);
    };
  }, [fetchStatus, isPageVisible, waitingRestart]);

  // 延迟监控轮询
  useEffect(() => {
    if (pingPaused || waitingRestart || !isPageVisible) return;
    fetchPing();
    pingTimerRef.current = setInterval(fetchPing, PING_POLL_INTERVAL_MS);
    return () => {
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    };
  }, [fetchPing, pingPaused, waitingRestart, isPageVisible]);

  // 进入重启等待模式
  const enterRestartWaiting = (type: string, seconds: number) => {
    setServerRestarting(true);
    setWaitingRestart(type);
    setCountdown(seconds);

    // 倒计时
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 延迟后开始探测服务恢复（给服务一些时间关闭和重启）
    const probeDelay = type === 'vps' ? 30000 : type === 'all' ? 15000 : 8000;
    if (probeTimeoutRef.current) clearTimeout(probeTimeoutRef.current);
    probeTimeoutRef.current = setTimeout(() => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const data = await serverControlApi.getStatus();
          // 服务恢复成功
          setStatus(data as any);
          exitRestartWaiting();
          message.success('服务已恢复正常');
        } catch {
          // 仍在重启中，继续等待
        }
      }, STATUS_RECOVERY_POLL_INTERVAL_MS);
    }, probeDelay);
  };

  // 退出重启等待模式
  const exitRestartWaiting = () => {
    setServerRestarting(false);
    setWaitingRestart(null);
    setCountdown(0);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    if (probeTimeoutRef.current) { clearTimeout(probeTimeoutRef.current); probeTimeoutRef.current = null; }
  };

  const handleRestartService = (serviceName: string) => {
    const label = SERVICE_LABELS[serviceName]?.label || serviceName;
    Modal.confirm({
      title: `确认重启${label}`,
      icon: <ExclamationCircleOutlined />,
      content: serviceName === 'backend'
        ? '重启后端服务会导致所有Bot短暂离线（约10-30秒），确定继续？'
        : serviceName === 'postgres'
        ? '重启数据库会导致所有服务短暂不可用，确定继续？'
        : `确定要重启${label}吗？`,
      okText: '确认重启',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setRestartingServices(prev => ({ ...prev, [serviceName]: true }));
        try {
          await serverControlApi.restartService(serviceName);
          if (serviceName === 'backend') {
            enterRestartWaiting('backend', 30);
          } else {
            message.success(`${label}重启指令已发送`);
            setTimeout(() => fetchStatus(), 5000);
          }
        } catch (e: any) {
          if (serviceName === 'backend') {
            // 后端自重启时请求可能中断，属正常现象
            enterRestartWaiting('backend', 30);
          } else {
            message.error(e?.response?.data?.detail || `重启${label}失败`);
          }
        } finally {
          setRestartingServices(prev => ({ ...prev, [serviceName]: false }));
        }
      },
    });
  };

  const handleRestartAll = () => {
    Modal.confirm({
      title: '确认重启所有服务',
      icon: <ExclamationCircleOutlined />,
      content: '将按依赖顺序重启所有Docker服务，期间系统将短暂不可用（约30-60秒）。确定继续？',
      okText: '全部重启',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setRestartingAll(true);
        try {
          await serverControlApi.restartAllServices();
        } catch {
          // 请求可能因服务重启而失败，属正常现象
        } finally {
          setRestartingAll(false);
        }
        // 无论请求成功或失败，都进入等待模式
        enterRestartWaiting('all', 60);
      },
    });
  };

  const handleRestartVps = () => {
    Modal.confirm({
      title: '确认重启VPS服务器',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            这将重启整台VPS服务器！所有服务将完全停止。
          </p>
          <p>VPS重启通常需要30-60秒，期间所有服务不可用。</p>
        </div>
      ),
      okText: '确认重启VPS',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setRestartingVps(true);
        try {
          await serverControlApi.restartVps();
        } catch (e: any) {
          // VPS重启API调用失败时才提示
          if (e?.response?.status && e.response.status !== 502) {
            message.error(e?.response?.data?.detail || 'VPS重启失败');
            setRestartingVps(false);
            return;
          }
        } finally {
          setRestartingVps(false);
        }
        enterRestartWaiting('vps', 90);
      },
    });
  };

  const handleToggleWithdrawalMaintenance = () => {
    const nextEnabled = !withdrawalMaintenanceEnabled;
    const actionText = nextEnabled ? '关闭提现' : '开启提现';
    Modal.confirm({
      title: `确认${actionText}` ,
      icon: <ExclamationCircleOutlined style={{ color: nextEnabled ? '#ff4d4f' : '#52c41a' }} />,
      content: nextEnabled
        ? '开启后，全站用户手动提现和自动提现都会被立即拦截，可用于紧急止损。'
        : '确认重新开放全站提现？请先确认风险问题已经处理完成。',
      okText: actionText,
      okType: nextEnabled ? 'danger' : 'primary',
      cancelText: '取消',
      onOk: async () => {
        setWithdrawalMaintenanceLoading(true);
        try {
          const result: any = await serverControlApi.setWithdrawalMaintenance(nextEnabled);
          setWithdrawalMaintenanceEnabled(Boolean(result?.enabled));
          message.success(result?.message || (nextEnabled ? '提现已关闭' : '提现已开启'));
        } catch (e: any) {
          message.error(e?.response?.data?.detail || `${actionText}失败`);
        } finally {
          setWithdrawalMaintenanceLoading(false);
        }
      },
    });
  };

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  // 延迟监控配置与辅助函数
  const PING_CFG: Record<string, { label: string; good: number; warn: number; maxVisual: number }> = {
    api: { label: 'API 响应', good: 50, warn: 200, maxVisual: 300 },
    database: { label: '数据库', good: 50, warn: 200, maxVisual: 300 },
    redis: { label: 'Redis', good: 50, warn: 200, maxVisual: 300 },
    telegram: { label: 'Telegram', good: 500, warn: 1500, maxVisual: 2000 },
  };

  const getPingTag = (key: string, ms: number, st: string) => {
    if (st === 'error') return <Tag color="error">错误</Tag>;
    const c = PING_CFG[key];
    if (ms < c.good) return <Tag color="success">好的</Tag>;
    if (ms < c.warn) return <Tag color="warning">一般</Tag>;
    return <Tag color="error">较差</Tag>;
  };

  const getPingChart = (data: number[], maxVisual: number) => ({
    animation: false,
    grid: { left: 0, right: 0, top: 0, bottom: 0 },
    xAxis: { type: 'category' as const, show: false, data: data.map((_: number, i: number) => i) },
    yAxis: { type: 'value' as const, show: false },
    visualMap: {
      show: false,
      type: 'continuous' as const,
      dimension: 1,
      min: 0,
      max: maxVisual,
      inRange: {
        color: ['#52c41a', '#a0d911', '#fadb14', '#faad14', '#ff7a45', '#ff4d4f'],
      },
    },
    series: [{
      type: 'line' as const,
      data,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2 },
      areaStyle: { opacity: 0.1 },
    }],
  });

  const getStatusTag = (state: string) => {
    if (state === 'running') return <Tag icon={<CheckCircleOutlined />} color="success">运行中</Tag>;
    if (state === 'exited') return <Tag icon={<CloseCircleOutlined />} color="error">已停止</Tag>;
    if (state === 'restarting') return <Tag icon={<ReloadOutlined spin />} color="processing">重启中</Tag>;
    return <Tag color="default">{state}</Tag>;
  };

  // 重启等待遮罩
  if (waitingRestart) {
    const labels: Record<string, string> = {
      backend: '后端服务重启中',
      all: '所有服务重启中',
      vps: 'VPS服务器重启中',
    };
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Result
          icon={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
          title={labels[waitingRestart] || '服务重启中'}
          subTitle={
            <div>
              <p>正在等待服务恢复，请勿关闭页面...</p>
              {countdown > 0 && (
                <p style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                  预计还需 {countdown} 秒
                </p>
              )}
              {countdown === 0 && (
                <p style={{ color: '#999' }}>正在检测服务状态...</p>
              )}
            </div>
          }
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="加载服务器状态..." />
      </div>
    );
  }

  const bandwidthUsedPercent = status?.vps
    ? Math.round((status.vps.data_counter / status.vps.plan_monthly_data) * 100)
    : 0;

  return (
    <div style={{ padding: '0' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>服务器管理</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchStatus}>刷新状态</Button>
        </Space>
      </div>

      <Card
        title="提现总开关"
        style={{ marginBottom: 16 }}
        extra={
          <Tag color={withdrawalMaintenanceEnabled ? 'error' : 'success'}>
            {withdrawalMaintenanceEnabled ? '提现已关闭' : '提现已开启'}
          </Tag>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type={withdrawalMaintenanceEnabled ? 'warning' : 'info'}
            showIcon
            message={withdrawalMaintenanceEnabled ? '当前处于提现维护状态' : '当前提现功能正常开放'}
            description={withdrawalMaintenanceEnabled
              ? '用户手动提现与自动提现都会被立即拦截，可用于支付/资金异常时的一键止损。'
              : '如发现支付或资金风控异常，可在这里一键关闭全站提现。'}
          />
          <Space>
            <Button
              type={withdrawalMaintenanceEnabled ? 'default' : 'primary'}
              danger={!withdrawalMaintenanceEnabled}
              icon={<PoweroffOutlined />}
              loading={withdrawalMaintenanceLoading}
              onClick={handleToggleWithdrawalMaintenance}
            >
              {withdrawalMaintenanceEnabled ? '重新开启提现' : '一键关闭提现'}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchStatus}>刷新开关状态</Button>
          </Space>
        </Space>
      </Card>

      {/* VPS信息卡片 */}
      {status?.bwg_configured && status?.vps && (
        <Card
          title={
            <Space>
              <CloudServerOutlined />
              <span>VPS服务器</span>
              {status.vps.ve_status === 'running'
                ? <Tag color="success">运行中</Tag>
                : <Tag color="error">{status.vps.ve_status}</Tag>
              }
            </Space>
          }
          extra={
            <Button
              type="primary"
              danger
              icon={<PoweroffOutlined />}
              loading={restartingVps}
              onClick={handleRestartVps}
            >
              重启VPS
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="主机名" value={status.vps.hostname || '-'} valueStyle={{ fontSize: 14 }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="IP地址" value={status.vps.ip || '-'} valueStyle={{ fontSize: 14 }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="机房位置" value={status.vps.node_location || '-'} valueStyle={{ fontSize: 14 }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="操作系统" value={status.vps.os || '-'} valueStyle={{ fontSize: 14 }} />
            </Col>
          </Row>
          <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12}>
              <Text type="secondary">月流量使用</Text>
              <Progress
                percent={bandwidthUsedPercent}
                format={() => `${formatBytes(status.vps!.data_counter)} / ${formatBytes(status.vps!.plan_monthly_data)}`}
                status={bandwidthUsedPercent > 90 ? 'exception' : 'normal'}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Text type="secondary">磁盘空间</Text>
              <Progress
                percent={status.vps.ve_used_disk_space_b && status.vps.plan_disk
                  ? Math.round((status.vps.ve_used_disk_space_b / status.vps.plan_disk) * 100)
                  : 0
                }
                format={() => `${formatBytes(status.vps!.ve_used_disk_space_b)} / ${formatBytes(status.vps!.plan_disk)}`}
              />
            </Col>
          </Row>
        </Card>
      )}

      {!status?.bwg_configured && (
        <Alert
          message="搬瓦工API未配置"
          description="在 .env 文件中设置 BWG_VEID 和 BWG_API_KEY 即可启用VPS远程管理。这两个值可以在搬瓦工 KiwiVM 控制面板中找到。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Docker服务卡片 */}
      <Card
        title={
          <Space>
            <AppstoreOutlined />
            <span>Docker服务</span>
            {status?.docker_available
              ? <Tag color="success">Socket已连接</Tag>
              : <Tag color="warning">Socket未挂载</Tag>
            }
          </Space>
        }
        extra={
          status?.docker_available && (
            <Button
              danger
              icon={<ReloadOutlined />}
              loading={restartingAll}
              onClick={handleRestartAll}
            >
              全部重启
            </Button>
          )
        }
      >
        {!status?.docker_available && (
          <Alert
            message="Docker Socket未挂载"
            description="仅支持重启后端服务（自重启）。如需管理其他服务，请在 docker-compose.yml 的 backend 服务中挂载 Docker socket。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {status?.docker_error && (
          <Alert
            message="Docker通信异常"
            description={status.docker_error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 16]}>
          {Object.entries(SERVICE_LABELS).map(([key, svc]) => {
            const svcStatus = status?.services?.[key];
            const isRunning = svcStatus?.status === 'running';
            const canRestart = key === 'backend' || status?.docker_available;

            return (
              <Col xs={24} sm={12} md={6} key={key}>
                <Card
                  size="small"
                  hoverable
                  style={{
                    borderColor: svcStatus
                      ? isRunning ? '#52c41a' : '#ff4d4f'
                      : '#d9d9d9',
                  }}
                >
                  <div style={{ textAlign: 'center', padding: '8px 0' }}>
                    <div style={{ fontSize: 28, marginBottom: 8, color: isRunning ? '#52c41a' : '#999' }}>
                      {svc.icon}
                    </div>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{svc.label}</div>
                    <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>{svc.description}</div>
                    {svcStatus
                      ? (
                        <>
                          {getStatusTag(svcStatus.status)}
                          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{svcStatus.status_detail}</div>
                        </>
                      )
                      : <Tag color="default">未检测到</Tag>
                    }
                    <div style={{ marginTop: 12 }}>
                      <Tooltip title={!canRestart ? 'Docker Socket未挂载，无法远程重启此服务' : ''}>
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<ReloadOutlined />}
                          loading={restartingServices[key]}
                          disabled={!canRestart}
                          onClick={() => handleRestartService(key)}
                        >
                          重启
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* 端点延迟监控 */}
      <Card
        title={
          <Space>
            <DashboardOutlined />
            <span>端点延迟监控</span>
          </Space>
        }
        extra={
          <Space>
            <Button size="small" onClick={() => setPingPaused(p => !p)}>
              {pingPaused ? '继续' : '暂停'}
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchPing}>刷新</Button>
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        <Row gutter={[16, 16]}>
          {Object.entries(PING_CFG).map(([key, cfg]) => {
            const info = latestPing[key];
            const ms = info?.latency_ms ?? -1;
            const data = pingHistory[key] || [];
            return (
              <Col xs={24} sm={12} md={6} key={key}>
                <Card size="small" hoverable>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{cfg.label}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <span style={{
                        fontSize: 28,
                        fontWeight: 'bold',
                        color: ms < 0 ? '#999' : ms < cfg.good ? '#52c41a' : ms < cfg.warn ? '#faad14' : '#ff4d4f',
                      }}>
                        {ms < 0 ? '--' : Math.round(ms)}
                      </span>
                      <span style={{ fontSize: 12, color: '#999' }}>MS</span>
                      {info && getPingTag(key, ms, info.status)}
                    </div>
                  </div>
                  {data.length > 1 && (
                    <ReactECharts
                      option={getPingChart(data, cfg.maxVisual)}
                      style={{ height: 60, marginTop: 8 }}
                      opts={{ renderer: 'svg' }}
                    />
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>
    </div>
  );
};

export default ServerControl;
